/**
 * JobIngestSettings v2: the settings surface for a job, ported from the mockup.
 *
 * Every section reads jobs/{jobId} through useFirestoreDoc and writes one field
 * per updateDoc: `ingestSettings.<field>` for the capture settings (the exact
 * path the live JobSettingsModal uses) and `registration.<field>` for the
 * Registration card (registration-v2 mockup frame 15; strategy Appendix B,
 * "Config"). Both go through the same write path below; no new write logic.
 * The one two-field write is the consent statement: new wording and its new
 * version go in a single update, so the form never serves new words under
 * the old version. That update runs in a transaction that reads the stored
 * text and version first, so two open tabs cannot both mint the same version.
 *
 * Categories and Backups are later; the note at the bottom keeps their place.
 */
import React, { useEffect, useState } from "react";
import { db, doc, updateDoc, runTransaction } from "../../../../firebase.js";
import { useFirestoreDoc } from "../../../../lib/useFirestore.js";
import RegistrationQr, { openPosterWindow, registrationUrl } from "./RegistrationQr.jsx";
import JobPresets from "./JobPresets.jsx";
import JobAutomation from "./JobAutomation.jsx";

const COUNTDOWN_PRESETS = [["Instant", 0], ["3 sec", 3], ["5 sec", 5], ["10 sec", 10]];
// The server's subject types (functions/src/types/ingest.ts SubjectTypeSchema).
// The list used to offer "object", which the server does not know: saving it
// stopped every arm until the server learned to fall back to "person".
const SUBJECT_TYPES = ["person", "prop", "vehicle", "location", "other"];

// The armed timeout: how long a guest loaded on the rig without Start holds
// it before a new arm may load someone else instead. The server takes a
// whole number from 10 to 600 and reads anything else as 90
// (functions/src/services/jobLookups.ts parseIngestSettings). A capture under
// way never times out.
const ARMED_TIMEOUT_MIN = 10;
const ARMED_TIMEOUT_MAX = 600;
const ARMED_TIMEOUT_DEFAULT = 90;
const ARMED_TIMEOUT_HINT = `Type a whole number of seconds from ${ARMED_TIMEOUT_MIN} to ${ARMED_TIMEOUT_MAX}. Nothing was saved.`;

// The value the rig uses for what is stored, the way the server reads it.
function armedTimeoutInUse(stored) {
  return Number.isInteger(stored) && stored >= ARMED_TIMEOUT_MIN && stored <= ARMED_TIMEOUT_MAX ? stored : ARMED_TIMEOUT_DEFAULT;
}

// A typed draft as the number to save, or null when it is not a whole number
// from 10 to 600 (then nothing is saved and the field shows the value in use).
function parseArmedTimeoutDraft(text) {
  const t = String(text ?? "").trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n >= ARMED_TIMEOUT_MIN && n <= ARMED_TIMEOUT_MAX ? n : null;
}
// The one line under the "Refuse captures while uploading" switch.
const UPLOAD_LOCK_HINT = "When on, the rig will not load a guest while the capture PC is uploading photos. Press Pause on the Rig tab and wait for Paused. Resume is by hand.";
const REGISTRATION_STATES = [["Draft", "draft"], ["Open", "open"], ["Closed", "closed"]];
// The six event accents from the June mockup (BRANDING_ACCENTS in the walkthrough).
const ACCENT_SWATCHES = ["#1A1A1A", "#2D9D4F", "#3B82F6", "#8B5CF6", "#D97706", "#E8758A"];
// Defaults when jobs/{jobId}.registration is absent (strategy Appendix B, "Config").
const REGISTRATION_DEFAULTS = {
  state: "draft",
  capacity: 400,
  accent: "#2D9D4F",
  contactEmail: "hello@newyorkcapture.com",
  consentVersion: "1",
};
const CONFIRM_ROTATE = "Printed posters with the old code stop working. Continue?";
// The Open chip needs a consent statement on file; shown in the State row instead of a write.
const OPEN_NEEDS_CONSENT_TEXT = "Add the consent statement before opening.";

function ingestSettingsPatch(field, value) {
  return { [`ingestSettings.${field}`]: value };
}

function registrationPatch(field, value) {
  return { [`registration.${field}`]: value };
}

// 16 random bytes as 32 hex characters: the job's public registration token.
function mintRegistrationToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// "1" becomes "2"; a version that is not a whole number restarts at "2".
function nextConsentVersion(version) {
  const n = Number.parseInt(String(version ?? ""), 10);
  return String(Number.isInteger(n) ? n + 1 : 2);
}

// New consent wording is a new version: one update carries consent.text and
// consent.version together. null when the trimmed draft matches the stored
// text, so nothing is written for an unchanged commit. The FIRST wording on a
// job (nothing stored yet) keeps the current version: it is version 1, not a
// replacement of one.
function consentUpdate(storedText, storedVersion, draft) {
  const text = String(draft ?? "").trim();
  const stored = String(storedText ?? "").trim();
  // An empty draft never writes: clearing the wording would silently close the
  // form (the server treats open-with-no-text as not open) and bump the version.
  if (!text || text === stored) return null;
  if (!stored) return registrationPatch("consent.text", text);
  return {
    ...registrationPatch("consent.text", text),
    ...registrationPatch("consent.version", nextConsentVersion(storedVersion)),
  };
}

// The State chips: Open needs a consent statement on file, so that click is
// refused with a message instead of a write. Returns { patch } or { refusal }.
function registrationStateChange(nextState, consentText) {
  if (nextState === "open" && !String(consentText ?? "").trim()) return { refusal: OPEN_NEEDS_CONSENT_TEXT };
  return { patch: registrationPatch("state", nextState) };
}

// A typed field that keeps a local draft and writes once, on blur (or Enter on
// a single line), so the job document is not written on every keystroke.
// onCommit may return false to reject the draft and show the stored value again.
function DraftField({ value, onCommit, textarea = false, ...rest }) {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => { setDraft(value ?? ""); }, [value]);
  const commit = () => {
    if (draft === (value ?? "")) return;
    if (onCommit(draft) === false) setDraft(value ?? "");
  };
  const Tag = textarea ? "textarea" : "input";
  return (
    <Tag value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={textarea ? undefined : (e) => { if (e.key === "Enter") e.currentTarget.blur(); }} {...rest} />
  );
}

// canAutomate (admins): the Automation card (JobAutomation.jsx), whose switches
// change only through the server (setJobAutomation), never through the writes below.
export default function JobIngestSettings({ jobId, canAutomate = false }) {
  const { data: job } = useFirestoreDoc(jobId ? `jobs/${jobId}` : null);
  const s = job?.ingestSettings ?? {};
  const reg = job?.registration ?? {};
  const [savedField, setSavedField] = useState(null); // one field path, or the paths one write covered
  const [error, setError] = useState(null);
  const [stateNote, setStateNote] = useState(null);
  const [timeoutNote, setTimeoutNote] = useState(null);
  const [showQr, setShowQr] = useState(false);

  // One write path for both maps, with one try/catch and one saved tick. The
  // Firestore call resolves to the key(s) the tick covers, or null when it
  // found nothing to write. The saved tick keys on the full field path.
  const commitWrite = async (writeFn) => {
    if (!jobId) return;
    setError(null);
    try {
      const key = await writeFn(doc(db, "jobs", jobId));
      if (key == null) return;
      setSavedField(key);
      setTimeout(() => setSavedField((f) => (f === key ? null : f)), 1500);
    } catch (e) { setError(e?.message ?? String(e)); }
  };
  const writePatch = (key, patch) => commitWrite(async (jobRef) => { await updateDoc(jobRef, patch); return key; });
  const write = (field, value) => writePatch(field, ingestSettingsPatch(field, value));
  const writeReg = (field, value) => writePatch(`registration.${field}`, registrationPatch(field, value));
  // The consent save computes its patch from the STORED text and version inside
  // a transaction, not from this tab's snapshot, so two tabs that both read
  // version 1 cannot both write version 2: the second one sees what the first
  // wrote and moves on to 3, or writes nothing when the wording already
  // matches. The keys of the patch it wrote decide the saved ticks: text only
  // for the first wording, text and version after that.
  const writeConsent = (draft) => commitWrite(async (jobRef) => {
    const patch = await runTransaction(db, async (tx) => {
      const snap = await tx.get(jobRef);
      const stored = snap.data()?.registration?.consent ?? {};
      const next = consentUpdate(stored.text, stored.version, draft);
      if (!next) return null;
      tx.update(jobRef, next);
      return next;
    });
    return patch ? Object.keys(patch) : null;
  });

  const countdown = Number(s.countdownSeconds ?? 0);
  // The armed timeout in use (90 when unset or not allowed), and whether the
  // stored value is one the rig does not use (the old field saved 0 and every
  // keystroke).
  const storedTimeout = s.armedTimeoutSeconds;
  const timeoutInUse = armedTimeoutInUse(storedTimeout);
  const storedTimeoutBad = storedTimeout != null && storedTimeout !== timeoutInUse;
  const regState = reg.state ?? REGISTRATION_DEFAULTS.state;
  const capacity = Number(reg.capacity ?? REGISTRATION_DEFAULTS.capacity);
  const accent = String(reg.accent ?? REGISTRATION_DEFAULTS.accent);
  const contactEmail = reg.contactEmail ?? REGISTRATION_DEFAULTS.contactEmail;
  const consentText = reg.consent?.text ?? "";
  const consentVersion = String(reg.consent?.version ?? REGISTRATION_DEFAULTS.consentVersion);
  const token = reg.publicToken ?? "";
  const formUrl = token ? registrationUrl(token) : "";
  const Saved = ({ field }) => ([].concat(savedField ?? []).includes(field) ? <span className="rigv2-saved">saved ✓</span> : null);

  // Saved on blur or Enter (DraftField), only a whole number from 10 to 600.
  const commitArmedTimeout = (v) => {
    const n = parseArmedTimeoutDraft(v);
    if (n === null) { setTimeoutNote(ARMED_TIMEOUT_HINT); return false; }
    setTimeoutNote(null);
    write("armedTimeoutSeconds", n);
    return true;
  };
  const commitCapacity = (v) => {
    const n = Math.round(Number(v));
    if (!Number.isFinite(n) || n < 1) return false;
    writeReg("capacity", n);
    return true;
  };
  const commitAccent = (v) => {
    const hex = v.trim();
    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return false;
    writeReg("accent", hex.toUpperCase());
    return true;
  };
  const changeState = (v) => {
    const change = registrationStateChange(v, consentText);
    setStateNote(change.refusal ?? null);
    if (change.patch) writePatch("registration.state", change.patch);
  };
  const commitConsent = (v) => {
    // Unchanged or empty after trimming: nothing to write, show the stored text
    // again. The patch that is written is computed again inside the transaction.
    if (!consentUpdate(consentText, consentVersion, v)) return false;
    setStateNote(null);
    writeConsent(v);
    return true;
  };
  const createCode = () => writeReg("publicToken", mintRegistrationToken());
  const rotateCode = () => {
    if (!window.confirm(CONFIRM_ROTATE)) return;
    setShowQr(false);
    writeReg("publicToken", mintRegistrationToken());
  };
  const printPoster = () => openPosterWindow({ url: formUrl, eventTitle: reg.eventTitle ?? "", hostLine: reg.hostLine ?? "" });

  return (
    <div className="rigv2-settings">
      {error && <div className="rigv2-banner error">{error}</div>}

      {canAutomate && jobId && <><JobAutomation key={jobId} jobId={jobId} /><JobPresets key={jobId} jobId={jobId} /></>}

      {/* Countdown, wired (same field as the live JobSettingsModal) */}
      <div className="rigv2-set-card">
        <div className="rigv2-set-head"><span className="rigv2-set-title">Capture timing</span><Saved field="countdownSeconds" /></div>
        <div className="rigv2-set-desc">How long the rig counts down before it fires. Instant = fire on Start.</div>
        <div className="rigv2-set-row">
          <span className="rigv2-presets">
            {COUNTDOWN_PRESETS.map(([label, v]) => (
              <button key={v} type="button" className={`rigv2-preset ${countdown === v ? "on" : ""}`} onClick={() => write("countdownSeconds", v)}>{label}</button>
            ))}
          </span>
          <input className="rigv2-num" type="number" min={0} max={60} value={countdown}
            onChange={(e) => write("countdownSeconds", Math.max(0, Math.min(60, Math.round(Number(e.target.value) || 0))))} />
          <span className="rigv2-set-label">seconds</span>
        </div>
      </div>

      {/* Capture options, wired operator toggles (live ingestSettings schema) */}
      <div className="rigv2-set-card">
        <div className="rigv2-set-head"><span className="rigv2-set-title">Capture options</span></div>
        <div className="rigv2-set-row">
          <span className="rigv2-set-label grow">Subject type</span>
          <select className="rigv2-select" value={s.subjectType ?? "person"} onChange={(e) => write("subjectType", e.target.value)}>
            {SUBJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <Saved field="subjectType" />
        </div>
        <div className="rigv2-set-row">
          <span className="rigv2-set-label grow">Allow walk-up captures</span>
          {/* On by default (item 2): allowed unless explicitly turned off. */}
          <input type="checkbox" className="rigv2-toggle" checked={s.allowWalkUp !== false} onChange={(e) => write("allowWalkUp", e.target.checked)} />
          <Saved field="allowWalkUp" />
        </div>
        <div className="rigv2-set-row">
          <span className="rigv2-set-label grow">Refuse captures while uploading</span>
          {/* Off by default: only true turns it on (functions/src/services/uploadLock.ts). */}
          <input type="checkbox" className="rigv2-toggle" aria-label="Refuse captures while uploading"
            checked={s.uploadLock === true} onChange={(e) => write("uploadLock", e.target.checked)} />
          <Saved field="uploadLock" />
        </div>
        <div className="rigv2-set-row">
          <span className="rigv2-set-hint grow">{UPLOAD_LOCK_HINT}</span>
        </div>
        <div className="rigv2-set-row">
          <span className="rigv2-set-label grow">Selfie required</span>
          <input type="checkbox" className="rigv2-toggle" checked={!!s.selfieRequired} onChange={(e) => write("selfieRequired", e.target.checked)} />
          <Saved field="selfieRequired" />
        </div>
        <div className="rigv2-set-row">
          <span className="rigv2-set-label grow">Armed timeout (seconds, {ARMED_TIMEOUT_MIN} to {ARMED_TIMEOUT_MAX})</span>
          <DraftField className="rigv2-num" type="number" inputMode="numeric" min={ARMED_TIMEOUT_MIN} max={ARMED_TIMEOUT_MAX} step={1}
            aria-label="Armed timeout in seconds" value={String(timeoutInUse)} onCommit={commitArmedTimeout} />
          <Saved field="armedTimeoutSeconds" />
        </div>
        {(timeoutNote || storedTimeoutBad) && (
          <div className="rigv2-set-row">
            <span className="rigv2-set-warn" role="alert">
              {timeoutNote || `The saved value (${String(storedTimeout)}) is not allowed, so the rig uses ${ARMED_TIMEOUT_DEFAULT}.`}
            </span>
            {storedTimeoutBad && (
              <button type="button" className="rigv2-set-sbtn" onClick={() => { setTimeoutNote(null); write("armedTimeoutSeconds", ARMED_TIMEOUT_DEFAULT); }}>
                Save {ARMED_TIMEOUT_DEFAULT}
              </button>
            )}
          </div>
        )}
        <div className="rigv2-set-row">
          <span className="rigv2-set-hint grow">
            After this long without Start, a new guest can be loaded in place of the one waiting. {ARMED_TIMEOUT_DEFAULT} unless changed. A capture under way never times out.
          </span>
        </div>
      </div>

      {/* Registration, wired: jobs/{jobId}.registration (mockup frame 15) */}
      <div className="rigv2-set-card rigv2-set-reg">
        <div className="rigv2-set-head"><span className="rigv2-set-title">Registration</span></div>
        <div className="rigv2-set-desc">What the public page shows, and whether it takes registrations. Every field here is read by a screen on the event day.</div>

        <div className="rigv2-set-row">
          <span className="rigv2-set-label rigv2-set-key">State</span>
          <span className="rigv2-presets">
            {REGISTRATION_STATES.map(([label, v]) => (
              <button key={v} type="button" className={`rigv2-preset ${regState === v ? "on" : ""}`} onClick={() => changeState(v)}>{label}</button>
            ))}
          </span>
          <span className="rigv2-set-hint">Takes effect on the next page load.</span>
          <Saved field="registration.state" />
          {(stateNote || (regState === "open" && !consentText.trim())) && (
            <span className="rigv2-set-warn" role="alert">{stateNote || OPEN_NEEDS_CONSENT_TEXT}</span>
          )}
        </div>

        <div className="rigv2-set-row">
          <span className="rigv2-set-label rigv2-set-key">Capacity</span>
          <DraftField className="rigv2-num" type="number" min={1} value={String(capacity)} onCommit={commitCapacity} />
          <span className="rigv2-set-hint">registrations, a hard stop, not a count of captures</span>
          <Saved field="registration.capacity" />
        </div>

        <div className="rigv2-set-row">
          <span className="rigv2-set-label rigv2-set-key">Event title</span>
          <DraftField className="rigv2-set-txt" type="text" value={reg.eventTitle ?? ""} placeholder="XR Brooklyn 2026" onCommit={(v) => writeReg("eventTitle", v.trim())} />
          <Saved field="registration.eventTitle" />
        </div>

        <div className="rigv2-set-row">
          <span className="rigv2-set-label rigv2-set-key">Host line</span>
          <DraftField className="rigv2-set-txt" type="text" value={reg.hostLine ?? ""} placeholder="Frank Benson + New York Capture" onCommit={(v) => writeReg("hostLine", v.trim())} />
          <Saved field="registration.hostLine" />
        </div>

        <div className="rigv2-set-row">
          <span className="rigv2-set-label rigv2-set-key">Accent color</span>
          <span className="rigv2-set-swatches">
            {ACCENT_SWATCHES.map((c) => (
              <button key={c} type="button" className={`rigv2-set-sw ${accent.toUpperCase() === c ? "on" : ""}`} style={{ background: c }} title={c} aria-label={`Accent ${c}`} onClick={() => writeReg("accent", c)} />
            ))}
          </span>
          <span className="rigv2-set-sw current" style={{ background: accent }} aria-hidden="true" />
          <DraftField className="rigv2-set-hex" type="text" value={accent} aria-label="Accent hex" onCommit={commitAccent} />
          <span className="rigv2-set-hint">Used on the form and the pass only.</span>
          <Saved field="registration.accent" />
        </div>

        <div className="rigv2-set-row">
          <span className="rigv2-set-label rigv2-set-key">Contact email</span>
          <DraftField className="rigv2-set-txt" type="email" value={contactEmail} onCommit={(v) => writeReg("contactEmail", v.trim())} />
          <Saved field="registration.contactEmail" />
          <span className="rigv2-set-hint wide">Shown on the form, the pass, and the closed message. Also the sender and the reply-to on the pass email.</span>
        </div>

        <div className="rigv2-set-row top">
          <span className="rigv2-set-label rigv2-set-key">Consent statement<br /><span className="rigv2-set-ver">version {consentVersion}</span></span>
          <DraftField textarea className="rigv2-set-area" rows={9} value={consentText} placeholder="The consent statement the form shows. The version 1 wording is in the strategy, Appendix C." onCommit={commitConsent} />
          <Saved field="registration.consent.text" />
        </div>
        <div className="rigv2-set-row">
          <span className="rigv2-set-label rigv2-set-key" />
          <span className="rigv2-set-hint grow">Saving new wording makes version {nextConsentVersion(consentVersion)}. Earlier registrations keep version {consentVersion}. Anyone with the old wording open on their phone is asked to read the new one and tick again.</span>
          <Saved field="registration.consent.version" />
        </div>

        <div className="rigv2-set-row">
          <span className="rigv2-set-label rigv2-set-key">Registration code</span>
          {token ? (
            <>
              <span className="mono rigv2-set-code">ends in {token.slice(-6)}</span>
              <button type="button" className={`rigv2-set-sbtn ${showQr ? "on" : ""}`} onClick={() => setShowQr((v) => !v)}>{showQr ? "Hide QR" : "Show QR"}</button>
              <button type="button" className="rigv2-set-sbtn" onClick={printPoster}>Print poster</button>
              <Saved field="registration.publicToken" />
              <button type="button" className="rigv2-set-sbtn danger rigv2-set-push" onClick={rotateCode}>Rotate code</button>
              <span className="rigv2-set-warn">Rotating the code makes every printed poster and the QR board stop working. Print and post new ones right after.</span>
            </>
          ) : (
            <>
              <button type="button" className="rigv2-set-sbtn" onClick={createCode}>Create code</button>
              <Saved field="registration.publicToken" />
              <span className="rigv2-set-hint">The poster and the QR board carry this code. Create it once, then print the poster.</span>
            </>
          )}
        </div>
        {token && showQr && (
          <div className="rigv2-set-qr">
            <RegistrationQr url={formUrl} size={220} label={formUrl} />
          </div>
        )}
      </div>

      {/* Later: the two mockup sections that still wait on design decisions. */}
      <div className="rigv2-set-later">Later: Categories (Guest / Crew / custom tags) and Backups (Move-to-client-folder + Traffic Control destination).</div>
    </div>
  );
}

export {
  ingestSettingsPatch, registrationPatch, nextConsentVersion, consentUpdate, registrationStateChange, OPEN_NEEDS_CONSENT_TEXT,
  SUBJECT_TYPES, UPLOAD_LOCK_HINT, ARMED_TIMEOUT_DEFAULT, ARMED_TIMEOUT_HINT, armedTimeoutInUse, parseArmedTimeoutDraft,
};
